import React, {
  forwardRef,
  Children,
  cloneElement,
  ReactElement,
  ReactNode,
  isValidElement,
} from 'react';
import { styled } from '../../../theme';
import {
  generateInputLabel,
  generateInputHint,
  generateInputStatusMessage,
} from '../component-generators';
import type {
  GDSCustomizableComponent,
  GDSResponsiveProp,
  GDSInputStatus,
} from '../../../types';
import { isReactElement, hasHiddenInput } from '../utilities';
import { GenesisCoreInspector } from '../../../test-utils/genesis-core-inspector';
import type { GDSHTMLAutocomplete } from '../types';

export interface GDSFormFieldProps extends GDSCustomizableComponent {
  children: ReactNode;
  hideLabel?: GDSResponsiveProp<boolean>;
  hint?: ReactNode;
  id: string;
  inputStatus?: GDSInputStatus;
  label: ReactNode;
  name?: string;
  required?: boolean;
  statusIconLabel?: string;
  statusMessage?: ReactNode;
  tooltip?: ReactNode;
  autoComplete?: GDSHTMLAutocomplete;
}

const DISPLAY_NAME = 'FormField';

const FormFieldBase = styled('div', { width: '100%' });

export const FormField = forwardRef<HTMLInputElement, GDSFormFieldProps>(
  (
    {
      children,
      className,
      css,
      hideLabel = false,
      hint,
      id,
      inputStatus,
      label,
      name,
      required = true,
      statusIconLabel,
      statusMessage,
      tooltip,
      autoComplete,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    const statusMessageId = `${id}-status-message`;
    const hintId = `${id}-hint`;

    const ariaDescribedBy: string[] = [];
    if (statusMessage) ariaDescribedBy.push(statusMessageId);
    if (hint) ariaDescribedBy.push(hintId);

    // Show warning if more than one child is passed
    if (Children.count(children) > 1)
      console.warn(
        'More than one child detected. FormField is a wrapper for a single input. For multiple inputs, use Fieldset.',
      );

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <FormFieldBase
          className={['GDS-form-field', className].join(' ')}
          css={css}
          ref={ref}
          data-testid={testId}
          {...(hasHiddenInput(children) && { 'aria-hidden': true })}
          {...props}
        >
          {label &&
            generateInputLabel({
              label,
              htmlFor: id,
              required,
              hideLabel,
              tooltip,
            })}

          {hint &&
            generateInputHint({
              hint,
              hintId,
              css: { marginBlockEnd: '$quarter' },
            })}

          <div className="GDS-input-wrapper">
            {Children.map(children, (child) => {
              // Clone attributes for React and HTML elements
              if (isValidElement(child)) {
                if (child?.props?.['aria-describedby'])
                  ariaDescribedBy.push(child.props['aria-describedby']);

                return cloneElement(child as ReactElement<any>, {
                  id,
                  name,
                  ...(ariaDescribedBy.length > 0 && {
                    'aria-describedby': ariaDescribedBy.join(' '),
                  }),
                  // Pass inputStatus only to React components, not to raw HTML elements like <input type="text"/>
                  ...(isReactElement(child) && { inputStatus, autoComplete }),
                });
              }

              return child;
            })}
          </div>

          <div aria-live="polite">
            {inputStatus &&
              statusMessage &&
              generateInputStatusMessage({
                id: statusMessageId,
                inputStatus,
                statusIconLabel,
                statusMessage,
              })}
          </div>
        </FormFieldBase>
      </GenesisCoreInspector>
    );
  },
);

FormField.displayName = DISPLAY_NAME;
